import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,

  HOUSEHOLD_APPLICATION_STATUS_COLUMNS,

  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
} from 'common/householdConstants';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
} from 'common/dataTableConstants';

import {
  getHouseholdQualification,
} from 'common/householdApplication/prequalificationHelpers';

import {
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

import {
  isApplicationPastStatus,
  getHouseholdEarliestApplicationStatus,
} from 'common/householdApplication/applicationHelpers';

import useDispatch from 'client/hooks/useDispatch';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrRenterHouseholdsModal from 'client/components/SrRenterHouseholdsModal';
import SrHouseholdKanbanBoardCard from 'client/components/SrHouseholdKanbanBoardCard';

const householdColumnTitleMap = {
  [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: 'wants to apply',
  [HOUSEHOLD_APPLICATION_STATUS_STARTED]: 'started',
  [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: 'completed',
  [HOUSEHOLD_APPLICATION_STATUS_PAID]: 'paid',
  [HOUSEHOLD_APPLICATION_STATUS_DELIVERED]: 'delivered',
  [HOUSEHOLD_APPLICATION_STATUS_ACCEPTED]: 'accepted',
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED]: 'signed',
};

export function sortHouseholds({ households }) {
  households.sort((householdA, householdB) => {
    const householdATs = new Date(householdA.leadRenter.frontLastMessageReceivedDate).getTime();
    const householdBTs = new Date(householdB.leadRenter.frontLastMessageReceivedDate).getTime();
    if (householdATs > householdBTs) {
      return -1;
    }
    if (householdATs < householdBTs) {
      return 1;
    }
    return 0;
  });

  return {
    activeHouseholds: households,
    backupHouseholds: [],
  };
}

export function sortPaidHouseholds(households, listingIdsWithAppsInReadyForLease) {
  const householdsById = {};
  const listingsById = {};
  const householdsIdsByListingId = {};
  const tentativelyQualifiedHouseholds = [];
  const notQualifiedHouseholds = [];
  const backupHouseholds = [];

  // Group data
  for (const household of households) {
    householdsById[household.id] = household;

    for (const listingApplication of household.listingApplications) {
      const {
        listing,
      } = listingApplication;

      listingsById[listing.id] = listing;
      householdsIdsByListingId[listing.id] = householdsIdsByListingId[listing.id] || [];
      householdsIdsByListingId[listing.id].push(household.id);
    }
  }

  // Oldest DOM listings at the top
  const listings = Object.values(listingsById)
    .sort((listingA, listingB) => listingB.daysOnMarket - listingA.daysOnMarket);

  for (const listing of listings) {
    const householdIdsApplyingToListing = householdsIdsByListingId[listing.id];
    for (const householdId of householdIdsApplyingToListing) {
      const household = householdsById[householdId];
      const listingApplication = household.listingApplications
        .find((listingApplication) => listingApplication.listing.id === listing.id);

      const householdApplicationStatus = getHouseholdEarliestApplicationStatus(
        listingApplication.applications
      );
      const hasHouseholdPassCompletedStatus = isApplicationPastStatus({
        status: householdApplicationStatus,
      }, HOUSEHOLD_APPLICATION_STATUS_COMPLETED);

      if (!hasHouseholdPassCompletedStatus) {
        continue;
      }

      if (listingIdsWithAppsInReadyForLease.includes(listing.id)) {
        backupHouseholds.push(household);
        continue;
      }

      const householdQualification = getHouseholdQualification({
        moveInDate: household.renterMoveInDate,
        listing,
        tenantScreeningCriteria: listingApplication.tenantScreeningCriteria,
        renters: household.users,
        householdApplications: listingApplication.applications,
        householdGuarantors: household.householdGuarantors,
        guarantorApplications: household.guarantorApplications,
        tenantAlertReports: household.tenantAlertReports,
        leadRenterId: household.leadRenter.id,
        partner: listingApplication.partner,
      });
      const isHouseholdQualified = householdQualification.isQualified;

      if (isHouseholdQualified) {
        tentativelyQualifiedHouseholds.push({
          ...household,
          listingApplications: [listingApplication],
        });
      }
      else {
        notQualifiedHouseholds.push({
          ...household,
          listingApplications: [listingApplication],
        });
      }
    }
  }

  return [
    ...tentativelyQualifiedHouseholds,
    ...notQualifiedHouseholds,
    ...backupHouseholds,
  ];
}

const SrHouseholdKanbanBoardColumn = ({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  accountManagerUserId,
  leasingManagerUserId,
  households,
  householdApplicationStatus,
  inProgress,
  listingIdsWithAppsInReadyForLease,
}) => {
  const dispatch = useDispatch();
  const [showArchivedHouseholds, setShowArchivedHouseholds] = useState(false);
  const [showHouseholdRenterId, setShowHouseholdRenterId] = useState(null);

  const isLeaseSignedColumn = householdApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED;
  const isPaidColumn = householdApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_PAID;

  const householdsSorted = isPaidColumn
    ? sortPaidHouseholds(households, listingIdsWithAppsInReadyForLease)
    : households;

  const reloadDataTableHousehold = (householdId) => {
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
      viewId: `household-id-${householdId}`,
      filter: {
        'households.id': householdId,
      },
      count: 1,
    });
  };

  return (
    <div className='householdKanbanBoardColumn'>
      <div className='columnName'>
        {householdColumnTitleMap[householdApplicationStatus]}
        {`${inProgress ? '' : `(${households.length})`}`}
      </div>
      <div className='columnCardContainer'>
        {inProgress && (
          <div className='loading-ellipses' />
        )}
        {householdsSorted.map((household) => (
          <SrHouseholdKanbanBoardCard
            key={household.id}
            household={household}
            dispatch={dispatch}
            householdApplicationStatus={householdApplicationStatus}
            householdAdminsList={[]}
            onClick={() => setShowHouseholdRenterId(household.leadRenterId)}
          />
        ))}
        <SrDataTableConnected
          itemsPropName='households'
          dataSource={DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN}
          rootViewId={householdApplicationStatus + HOUSEHOLD_LISTING_STATUS_ARCHIVED}
          count={20}
          initialViewSettings={{
            includeItemsCount: true,
            sort: {},
            filter: {},
            customParams: {
              householdKanbanApplicationColumn: {
                moveInDateRanges,
                listingManagementCompany,
                listingServices,
                accountManagerUserId,
                leasingManagerUserId,
                householdListingStatus: HOUSEHOLD_LISTING_STATUS_ARCHIVED,
                ...(HOUSEHOLD_APPLICATION_STATUS_COLUMNS
                  .find((c) => c.householdApplicationStatus === householdApplicationStatus) || {}),
              },
            },
          }}
        >
          {(archivedHouseholdsDataTable) => {
            const showArchivedHouseholdsText = (
              !isLeaseSignedColumn
              && archivedHouseholdsDataTable.householdsCount > 0
            );

            const ref = (archivedHouseholdsDataTable.householdsCount === null || showArchivedHouseholds)
              ? archivedHouseholdsDataTable.onInfiniteScrollWrapperRef
              : null;

            return (
              <div ref={ref}>
                {showArchivedHouseholdsText && (
                  <div
                    className='archivedText'
                    onClick={() => setShowArchivedHouseholds(!showArchivedHouseholds)}
                  >
                    {showArchivedHouseholds
                      ? 'Archived'
                      : `View ${archivedHouseholdsDataTable.householdsCount} archived`
                    }
                  </div>
                )}
                {!showArchivedHouseholds
                  ? null
                  : archivedHouseholdsDataTable.households.map((household) => (
                    <SrHouseholdKanbanBoardCard
                      archived
                      key={household.id}
                      household={household}
                      dispatch={dispatch}
                      householdApplicationStatus={householdApplicationStatus}
                      householdAdminsList={[]}
                      onClick={() => setShowHouseholdRenterId(household.leadRenterId)}
                    />
                  ))}
              </div>
            );
          }}
        </SrDataTableConnected>
      </div>
      {showHouseholdRenterId && (
        <SrRenterHouseholdsModal
          renter={{ id: showHouseholdRenterId }}
          onClose={() => setShowHouseholdRenterId(null)}
          householdAdminsList={[]}
          reloadDataTableHousehold={reloadDataTableHousehold}
        />
      )}
    </div>
  );
};

SrHouseholdKanbanBoardColumn.propTypes = {
  moveInDateRanges: PropTypes.array,
  listingManagementCompany: PropTypes.array,
  listingServices: PropTypes.array,
  accountManagerUserId: PropTypes.array,
  households: PropTypes.array,
  householdApplicationStatus: PropTypes.string,
  inProgress: PropTypes.bool,
};

SrHouseholdKanbanBoardColumn.displayName = 'SrHouseholdKanbanBoardColumn';

export default SrHouseholdKanbanBoardColumn;
